import { useEffect } from 'react';

import { IMPORTS_IN_PROGRESS_STORE } from 'state/imports/importsInProgress';

import { isActiveImportStatus } from '../types';
import { useImportsList } from './useImportsList';

const SEED_POLL_INTERVAL_MS = 5000;

// Keeps `IMPORTS_IN_PROGRESS_STORE` in sync with real import runs, since live SignalR events might be
// missed (if user opens a project and misses the start, or a dropped `ImportCompleted` because of a SignalR disconnect).
// Only poll for imports we missed live (or lost track of after a reconnect); imports caught live via
// `ImportStarted` will get their `ImportCompleted` on the same connection, so they don't need polling.
export function useActiveImports(projectId: string) {
  const hasImportNeedingPolling = IMPORTS_IN_PROGRESS_STORE((state) =>
    Object.values(state.importsInProgress).some((item) => item.projectId === projectId && item.needsPolling)
  );

  const { data: imports, isError } = useImportsList(projectId, {
    refetchInterval: hasImportNeedingPolling ? SEED_POLL_INTERVAL_MS : false
  });

  useEffect(() => {
    if (!projectId) {
      return;
    }

    if (isError) {
      IMPORTS_IN_PROGRESS_STORE.getState().markProjectLoaded(projectId);
      return;
    }

    if (!imports) {
      return;
    }

    const activeImports = imports
      .filter((run) => isActiveImportStatus(run.runStatus))
      .map((run) => ({
        importRunId: run.id,
        projectId: run.projectId,
        proposalId: run.proposalId ?? undefined
      }));

    IMPORTS_IN_PROGRESS_STORE.getState().syncActiveImports(
      projectId,
      activeImports,
      imports.map((run) => run.id)
    );
  }, [projectId, imports, isError]);
}
